import { useState } from 'react';
import { X, Mail, Lock, User, Eye, EyeOff, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AuthModal = ({ isOpen, onClose, onAuthSuccess }) => {
  const [mode, setMode] = useState('login');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.email || !formData.password) {
      toast.error('Please fill all required fields');
      return;
    }

    if (mode === 'register' && !formData.full_name) {
      toast.error('Please enter your name');
      return;
    }

    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setLoading(true);
    try {
      const endpoint = mode === 'login' ? '/auth/login' : '/auth/register';
      const payload = mode === 'login' 
        ? { email: formData.email, password: formData.password }
        : formData;

      const response = await axios.post(`${API}${endpoint}`, payload);
      
      localStorage.setItem('token', response.data.access_token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      
      toast.success(mode === 'login' ? 'Welcome back!' : 'Account created successfully!');
      onAuthSuccess?.(response.data.user);
      onClose();
      
      setFormData({ email: '', password: '', full_name: '' });
    } catch (error) {
      const message = error.response?.data?.detail || 
        (mode === 'login' ? 'Invalid credentials' : 'Registration failed');
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-charcoal/60 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div 
        className="bg-ivory max-w-md w-full overflow-hidden shadow-[0_25px_50px_-12px_rgba(46,42,38,0.25)]"
        onClick={(e) => e.stopPropagation()}
        data-testid="auth-modal"
      >
        {/* Header */}
        <div className="px-8 pt-8 pb-6 border-b border-charcoal/10">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="font-heading text-3xl font-light text-charcoal">
                {mode === 'login' ? 'Welcome Back' : 'Create Account'}
              </h2>
              <p className="text-sm text-charcoal-light mt-2">
                {mode === 'login' 
                  ? 'Sign in to your account' 
                  : 'Join the JAT Fashion family'}
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-charcoal-light hover:text-charcoal transition-colors"
              data-testid="close-auth-modal"
            >
              <X className="w-5 h-5" strokeWidth={1.5} />
            </button>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-8 space-y-6">
          {/* Name Field (Register only) */}
          {mode === 'register' && (
            <div>
              <label className="block text-xs tracking-[0.1em] uppercase text-charcoal-light mb-3">
                Full Name
              </label>
              <div className="relative">
                <User className="absolute left-0 top-1/2 -translate-y-1/2 w-4 h-4 text-charcoal-light" strokeWidth={1.5} />
                <input
                  type="text"
                  value={formData.full_name}
                  onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                  placeholder="Enter your name"
                  className="w-full pl-7 pr-4 py-3 border-b border-charcoal/20 bg-transparent focus:outline-none focus:border-gold text-charcoal placeholder:text-charcoal-light/50 transition-colors"
                  data-testid="auth-name-input"
                />
              </div>
            </div>
          )}

          {/* Email Field */}
          <div>
            <label className="block text-xs tracking-[0.1em] uppercase text-charcoal-light mb-3">
              Email Address
            </label>
            <div className="relative">
              <Mail className="absolute left-0 top-1/2 -translate-y-1/2 w-4 h-4 text-charcoal-light" strokeWidth={1.5} />
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="Enter your email"
                className="w-full pl-7 pr-4 py-3 border-b border-charcoal/20 bg-transparent focus:outline-none focus:border-gold text-charcoal placeholder:text-charcoal-light/50 transition-colors"
                data-testid="auth-email-input"
              />
            </div>
          </div>

          {/* Password Field */}
          <div>
            <label className="block text-xs tracking-[0.1em] uppercase text-charcoal-light mb-3">
              Password
            </label>
            <div className="relative">
              <Lock className="absolute left-0 top-1/2 -translate-y-1/2 w-4 h-4 text-charcoal-light" strokeWidth={1.5} />
              <input
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                placeholder={mode === 'login' ? 'Enter your password' : 'Create a password'}
                className="w-full pl-7 pr-10 py-3 border-b border-charcoal/20 bg-transparent focus:outline-none focus:border-gold text-charcoal placeholder:text-charcoal-light/50 transition-colors"
                data-testid="auth-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-0 top-1/2 -translate-y-1/2 p-1 text-charcoal-light hover:text-charcoal transition-colors"
              >
                {showPassword ? <EyeOff className="w-4 h-4" strokeWidth={1.5} /> : <Eye className="w-4 h-4" strokeWidth={1.5} />}
              </button>
            </div>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-charcoal text-ivory py-4 text-xs tracking-[0.15em] uppercase font-medium flex items-center justify-center gap-2 hover:bg-charcoal/90 transition-colors disabled:opacity-50"
            data-testid="auth-submit-btn"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                {mode === 'login' ? 'Signing in...' : 'Creating account...'}
              </>
            ) : (
              mode === 'login' ? 'Sign In' : 'Create Account'
            )}
          </button>

          {/* Switch Mode */}
          <div className="text-center pt-2">
            <p className="text-sm text-charcoal-light">
              {mode === 'login' ? "Don't have an account?" : 'Already have an account?'}
              <button
                type="button"
                onClick={() => setMode(mode === 'login' ? 'register' : 'login')}
                className="ml-2 text-gold font-medium hover:text-gold-light transition-colors"
              >
                {mode === 'login' ? 'Sign Up' : 'Sign In'}
              </button>
            </p>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AuthModal;
